import {cn} from '@/lib/utils';
import {useCallback, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {Button} from '@/components/app/button';
import {Modal} from '@/components/app/modal';
import {logout} from '../../auth/authSlice';
import {Timer} from '../../../app/components/Timer';
import {useAppDispatch} from '../../../app/hooks/useAppDispatch';
import {api} from '../../../app/services/api';
import {useRefreshTokenMutation} from '../../../app/services/identitiesApi';
import {toastError} from '../../../app/toast';
import {useBeforeSessionExpire} from '../hooks/useBeforeSessionExpire';
import {useDemo} from '../../demo/hooks/useDemo';
export const BeforeSessionExpire = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [refreshToken, {isLoading: isLoadingRefreshToken}] = useRefreshTokenMutation();
  const [visible, setVisible] = useState(false);
  const timer = useRef<Timer>(null);
  const {isDemo, showLimitReachedDialog} = useDemo();
  const threshold = 180;
  const thresholdShift = 60;
  const [timerDuration, setTimerDuration] = useState(threshold * 1000);
  const signOut = useCallback(() => {
    navigate('/sign-in');
    dispatch(logout());
    dispatch(api.util.resetApiState());
  }, []);
  useBeforeSessionExpire(
    5,
    () => {
      showLimitReachedDialog({
        closable: false,
        message: 'Please create an account to continue.',
        warning: 'Your demo session has expired.',
        title: 'Demo Session Timeout',
      });
    },
    isDemo,
  );
  useBeforeSessionExpire(
    thresholdShift,
    () => {
      signOut();
    },
    !isDemo,
  );
  useBeforeSessionExpire(
    threshold + thresholdShift,
    ({remainingTime}) => {
      if (remainingTime > thresholdShift) {
        setTimerDuration((remainingTime - thresholdShift) * 1000);
        setVisible(true);
      } else {
        signOut();
      }
    },
    !isDemo,
  );
  return (
    <Modal
      className={'w-[30rem]'}
      closable={false}
      footer={
        <>
          <Button
            className={cn('text-muted-foreground', 'rounded-full')}
            disabled={isLoadingRefreshToken}
            onClick={() => signOut()}
            variant="ghost"
            type="button"
          >
            {'Close'}
          </Button>
          <Button
            isLoading={isLoadingRefreshToken}
            onClick={async () => {
              timer.current?.pause();
              try {
                await refreshToken().unwrap();
                setVisible(false);
              } catch (e) {
                timer.current?.start();
                toastError(e);
              }
            }}
            className="rounded-full"
            type="button"
          >
            {'Continue'}
          </Button>
        </>
      }
      title={'Session Timeout'}
      onClose={() => setVisible(false)}
      open={visible}
    >
      {visible && (
        <Timer
          duration={timerDuration}
          onEnd={() => signOut()}
          ref={timer}
          render={({remainingTime}) => {
            const expirationDate = new Date(Date.now() + remainingTime);
            const remainingMinutes = Math.floor(remainingTime / 60000);
            const remainingSeconds = Math.floor((remainingTime % 60000) / 1000);
            if (remainingMinutes === 0 && remainingSeconds === 0) {
              return <div className={'leading-normal'}>Your session has expired.</div>;
            }
            return (
              <div className={'leading-normal'}>
                Your session will be expired in{' '}
                <strong>
                  {remainingMinutes === 0 ? '' : remainingMinutes === 1 ? `1 minute` : `${remainingMinutes} minutes`}
                  {remainingMinutes > 0 && remainingSeconds > 0 ? ' and ' : ''}
                  {remainingSeconds === 0 ? '' : remainingSeconds === 1 ? '1 second' : `${remainingSeconds} seconds`}
                </strong>
                .
                <br />
                <time className={'text-muted-foreground'} dateTime={expirationDate.toISOString()}>
                  (
                  {expirationDate.toLocaleString(undefined, {
                    dateStyle: 'long',
                    timeStyle: 'long',
                  })}
                  )
                </time>
              </div>
            );
          }}
        />
      )}
    </Modal>
  );
};
